import { TranslocoService } from '@jsverse/transloco';
import { translateLookupLabel } from '../../../../_shared/helpers/lookup-label.helper';

/**
 * Compliance catalogue names (periodic frequencies, routine types, checklist types) come from the
 * backend as English display names that double as values. These helpers translate them for DISPLAY
 * only, by slug under a stable namespace; callers keep comparisons and payloads on the raw name.
 */

export const COMPLIANCE_FREQUENCY_LABEL_PREFIX = 'compliance.shared.frequencies';
export const COMPLIANCE_ROUTINE_TYPE_LABEL_PREFIX = 'compliance.shared.routineTypes';
export const COMPLIANCE_CHECKLIST_TYPE_LABEL_PREFIX = 'compliance.shared.checklistTypes';

/** Separators the API uses to join composite names, e.g. "Pre-Trip / Post-Trip" or "Inspection, Document". */
const COMPOSITE_NAME_SEPARATOR = /(\s*[\/,&]\s*)/;

/**
 * Translates a catalogue name that may be a composite of several names. The whole name wins when it
 * has a key of its own; otherwise each segment is translated on its own and the separators are kept.
 * Segments without a key stay as the raw backend text.
 */
export function translateCompositeLookupLabel(
  transloco: TranslocoService,
  prefixes: string | string[],
  name: string | null | undefined
): string {
  const raw = name ?? '';
  const prefixList = Array.isArray(prefixes) ? prefixes : [prefixes];
  if (!raw.trim()) return raw;

  const whole = translateFirstMatch(transloco, prefixList, raw);
  if (whole !== raw) return whole;

  const parts = raw.split(COMPOSITE_NAME_SEPARATOR);
  if (parts.length === 1) return raw;

  return parts
    .map((part, index) => (index % 2 === 0 ? translateFirstMatch(transloco, prefixList, part) : part))
    .join('');
}

/** Periodic compliance frequency ("Weekly", "Every 2 Years", …). */
export function translateComplianceFrequency(transloco: TranslocoService, name: string | null | undefined): string {
  return translateLookupLabel(transloco, COMPLIANCE_FREQUENCY_LABEL_PREFIX, name);
}

/** Routine type ("Pre-Trip", "Pre-Trip / Post-Trip", "Pre-Work Order", …). */
export function translateRoutineTypeName(transloco: TranslocoService, name: string | null | undefined): string {
  return translateCompositeLookupLabel(transloco, COMPLIANCE_ROUTINE_TYPE_LABEL_PREFIX, name);
}

/** Checklist type ("Inspection", "Document", or a composite such as "Inspection / Document"). */
export function translateChecklistTypeName(transloco: TranslocoService, name: string | null | undefined): string {
  return translateCompositeLookupLabel(transloco, COMPLIANCE_CHECKLIST_TYPE_LABEL_PREFIX, name);
}

/**
 * A compliance's schedule name is a frequency for periodic compliances and a routine type for
 * routine ones; use this where the compliance type is not known up front.
 */
export function translateComplianceScheduleName(transloco: TranslocoService, name: string | null | undefined): string {
  return translateCompositeLookupLabel(
    transloco,
    [COMPLIANCE_FREQUENCY_LABEL_PREFIX, COMPLIANCE_ROUTINE_TYPE_LABEL_PREFIX],
    name
  );
}

/** Any compliance catalogue name (checklist type, routine type or frequency) when its kind is unknown. */
export function translateComplianceCatalogueName(transloco: TranslocoService, name: string | null | undefined): string {
  return translateCompositeLookupLabel(
    transloco,
    [COMPLIANCE_CHECKLIST_TYPE_LABEL_PREFIX, COMPLIANCE_ROUTINE_TYPE_LABEL_PREFIX, COMPLIANCE_FREQUENCY_LABEL_PREFIX],
    name
  );
}

function translateFirstMatch(transloco: TranslocoService, prefixes: string[], name: string): string {
  for (const prefix of prefixes) {
    const translated = translateLookupLabel(transloco, prefix, name);
    if (translated !== name) return translated;
  }

  return name;
}